import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { assignPaymentToPatient } from 'api/Billing';

import useModal from 'lib/hooks/useModal';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { getProceduresTotal } from 'components/public/lib/utils';
import Popover from 'components/shared/Popovers/Popover/Popover';
import { currentPractice } from 'components/practice/practiceState';
import AssignPatientModal from 'components/practice/shared/components/AssignPatientModal';

import { getInvoicesFromSelectedRows } from '../lib/utils';

import NewInvoice from './NewInvoice';
import { InvoiceModal } from './InvoiceModal';
import NewInvoiceView from './NewInvoiceView';
import InvoicePrintView from './InvoicePrintView';

const InternalInvoicesNavigatePopover = ({ data }) => {
  let finalGroupInvoices = [];
  if (data) {
    finalGroupInvoices = getInvoicesFromSelectedRows([data]);
  }

  const pageToPrintRef = useRef();
  const navigate = useNavigate();
  const {
    isOpen: isInvoiceModalOpen,
    openModal: openInvoiceModal,
    closeModal: closeInvoiceModal
  } = useModal();

  const {
    isOpen: isPatientModalOpen,
    openModal: openPatientModal,
    closeModal: closePatientModal
  } = useModal();

  const practice = useRecoilValue(currentPractice);
  const claims = practice?.display_settings?.claims;
  const { patient_id, appointment_id, eraIds = [] } = data || {};

  const modalContent = (
    <>
      {finalGroupInvoices.map((finalInvoice, idx) => {
        const invoiceDetails = getProceduresTotal([finalInvoice]);
        return (
          <div key={idx}>
            <InvoicePrintView
              patient={finalInvoice?.patient}
              practice={practice}
              createdAt={finalInvoice?.dateofservice}
              content={
                <NewInvoice invoice={finalInvoice} isView>
                  <NewInvoiceView hasClaims={claims} invoiceDetails={invoiceDetails} />
                </NewInvoice>
              }
            />
          </div>
        );
      })}
    </>
  );

  const { mutate: mutateAssignToPatient, isLoading } = useCustomMutation({
    mutationFn: (patientId) =>
      assignPaymentToPatient(navigate, { refId: data?.id, refType: 'invoice', patientId }),
    invalidateQueryKey: ['payments', 'internal_invoices'],
    successMessage: 'Invoice assigned to patient',
    errorMessage: "Couldn't assign invoice to patient",
    onSuccess: () => closePatientModal()
  });

  if (!data) return null;

  const options = [
    { label: 'Preview invoice', onClick: () => openInvoiceModal(), icon: 'eye-mono' },
    {
      label: 'View patient',
      onClick: () => window.open(`/portal/charts/${patient_id}/billing/invoices`, '_blank'),
      icon: 'user-2-mono'
    },
    appointment_id && {
      label: 'View appointment',
      onClick: () => window.open(`/portal/appointments?appointment_id=${appointment_id}`, '_blank'),
      icon: 'calendar-tick-mono'
    },
    eraIds?.length > 0 && {
      label: 'View ERA',
      icon: 'wallet-mono',
      children: eraIds.map((eraId) => ({
        label: eraId,
        onClick: () => window.open(`/portal/beyond-billing/era/${eraId}`, '_blank')
      }))
    },
    !patient_id && {
      label: 'Assign patient',
      onClick: openPatientModal,
      icon: 'user-circle-add-mono'
    }
  ].filter(Boolean);

  return (
    <>
      <Popover
        className="flex h-full w-full items-center justify-center"
        position="left"
        isFixed
        icon="context-menu-horizontal"
        options={options}
        isBorder={false}
      />

      {isInvoiceModalOpen && (
        <InvoiceModal
          // prevButton={prevButton}
          title="Preview invoice"
          invoiceModalVisible={isInvoiceModalOpen}
          onClose={closeInvoiceModal}
          modalContent={
            <div ref={pageToPrintRef} className="h-full w-auto">
              {modalContent}
            </div>
          }
        />
      )}
      {isPatientModalOpen && (
        <AssignPatientModal
          open={isPatientModalOpen}
          setOpen={closePatientModal}
          loading={isLoading}
          onAssign={(patient) => {
            if (!patient?.id) return;
            mutateAssignToPatient(patient?.id);
          }}
        />
      )}
    </>
  );
};

export default InternalInvoicesNavigatePopover;
